import {
  AlertTriangle,
  Building2,
  Check,
  CreditCard,
  Edit3,
  Eye,
  Loader2,
  Mail,
  MapPin,
  Phone,
  Plus,
  RefreshCw,
  Router,
  Search,
  ShieldCheck,
  Trash2,
  UserRound,
  Users,
  Wifi,
  X
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { API_CONFIG, fetchApp, fetchData } from "../../services/apiService";
import ClientDetailPage from "./ClientsDetails";

interface Client {
  id: number;
  name: string;
  contact_email: string;
  contact_phone?: string;
  address?: string;
  mpesa_paybill?: string;
  users?: any[];
  routers?: any[];
}

interface ClientFormData {
  name: string;
  contact_email: string;
  contact_phone?: string;
  address?: string;
  mpesa_paybill?: string;
}

function cn(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ");
}

function formatNumber(value?: number | null) {
  return new Intl.NumberFormat("en-KE").format(Number(value || 0));
}

function getInitials(name?: string) {
  if (!name) return "C";

  const initials = name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

  return initials || "C";
}

function hasValue(value?: string | null) {
  return Boolean(value && value.trim());
}

function StatCard({
  title,
  value,
  helper,
  icon,
  tone,
}: {
  title: string;
  value: number;
  helper: string;
  icon: React.ReactNode;
  tone: string;
}) {
  return (
    <div className="group relative overflow-hidden rounded-3xl border border-white/80 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg">
      <div
        className={cn(
          "absolute right-[-34px] top-[-34px] h-24 w-24 rounded-full opacity-15 blur-2xl",
          tone,
        )}
      />

      <div className="relative flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-slate-500">{title}</p>

          <h3 className="mt-1 text-3xl font-black tracking-tight text-slate-950">
            {formatNumber(value)}
          </h3>

          <p className="mt-1 text-xs font-medium text-slate-400">{helper}</p>
        </div>

        <div
          className={cn(
            "rounded-2xl p-3 text-white shadow-lg transition group-hover:scale-105",
            tone,
          )}
        >
          {icon}
        </div>
      </div>
    </div>
  );
}

function ClientAvatar({ name }: { name: string }) {
  return (
    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-950 text-sm font-black text-white shadow-sm">
      {getInitials(name)}
    </div>
  );
}

function ContactLine({
  icon,
  value,
  fallback = "Not set",
}: {
  icon: React.ReactNode;
  value?: string | null;
  fallback?: string;
}) {
  return (
    <div className="flex min-w-0 items-center gap-2 text-sm text-slate-600">
      <span className="shrink-0 text-slate-400">{icon}</span>
      <span
        className={cn(
          "truncate",
          hasValue(value) ? "font-medium text-slate-700" : "text-slate-400",
        )}
      >
        {hasValue(value) ? value : fallback}
      </span>
    </div>
  );
}

function ResourceBadge({
  icon,
  label,
  value,
  className,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  className: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-bold",
        className,
      )}
    >
      {icon}
      {formatNumber(value)} {label}
    </span>
  );
}

function EmptyState({
  searchTerm,
  onCreate,
}: {
  searchTerm: string;
  onCreate: () => void;
}) {
  return (
    <div className="rounded-3xl border border-dashed border-slate-200 bg-white px-4 py-16 text-center shadow-sm">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-100 text-slate-400">
        <Users size={30} />
      </div>

      <h3 className="mt-4 text-lg font-black text-slate-950">
        {searchTerm ? "No clients found" : "No clients yet"}
      </h3>

      <p className="mx-auto mt-1 max-w-sm text-sm font-medium leading-6 text-slate-500">
        {searchTerm
          ? "Try searching using a different client name, email, phone, address, or paybill number."
          : "Create your first client to start managing routers, users, billing details, and network resources."}
      </p>

      {!searchTerm && (
        <button
          onClick={onCreate}
          className="mt-5 inline-flex items-center gap-2 rounded-2xl bg-slate-950 px-5 py-2.5 text-sm font-black text-white shadow-sm transition hover:bg-slate-800"
        >
          <Plus size={17} />
          Add first client
        </button>
      )}
    </div>
  );
}

function ClientFormModal({
  open,
  mode,
  formData,
  formErrors,
  submitting,
  onClose,
  onSubmit,
  onChange,
}: {
  open: boolean;
  mode: "create" | "edit";
  formData: ClientFormData;
  formErrors: { [key: string]: string };
  submitting: boolean;
  onClose: () => void;
  onSubmit: (event: React.FormEvent) => void;
  onChange: (next: ClientFormData) => void;
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-950/50 px-4 py-6 backdrop-blur-sm">
      <div className="max-h-[92vh] w-full max-w-2xl overflow-hidden rounded-[2rem] bg-white shadow-2xl">
        <div className="relative overflow-hidden bg-slate-950 px-5 py-5 text-white">
          <div className="absolute right-[-70px] top-[-70px] h-40 w-40 rounded-full bg-blue-500/30 blur-3xl" />

          <div className="relative flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="rounded-2xl bg-white p-3 text-slate-950 shadow-lg">
                <Building2 size={22} />
              </div>

              <div>
                <h2 className="text-xl font-black">
                  {mode === "create" ? "Add New Client" : "Edit Client"}
                </h2>

                <p className="mt-1 text-sm leading-5 text-white/60">
                  {mode === "create"
                    ? "Create an organisation profile with contact and billing details."
                    : "Update this client’s organisation, contact, and paybill information."}
                </p>
              </div>
            </div>

            <button
              onClick={onClose}
              type="button"
              className="rounded-2xl bg-white/10 p-2 text-white/70 transition hover:bg-white/15 hover:text-white"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          className="max-h-[calc(92vh-110px)] overflow-y-auto px-5 py-5"
        >
          <div className="grid gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <label className="mb-1.5 block text-xs font-black uppercase tracking-wide text-slate-500">
                Client Name <span className="text-rose-500">*</span>
              </label>

              <input
                type="text"
                value={formData.name}
                onChange={(event) =>
                  onChange({ ...formData, name: event.target.value })
                }
                className={cn(
                  "w-full rounded-2xl border bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-800 outline-none transition focus:bg-white focus:ring-4",
                  formErrors.name
                    ? "border-rose-300 focus:border-rose-400 focus:ring-rose-100"
                    : "border-slate-200 focus:border-blue-400 focus:ring-blue-100",
                )}
                placeholder="Example: AAMX Billing"
              />

              {formErrors.name && (
                <p className="mt-1.5 text-xs font-bold text-rose-600">
                  {formErrors.name}
                </p>
              )}
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-black uppercase tracking-wide text-slate-500">
                Email <span className="text-rose-500">*</span>
              </label>

              <input
                type="email"
                value={formData.contact_email}
                onChange={(event) =>
                  onChange({
                    ...formData,
                    contact_email: event.target.value,
                  })
                }
                className={cn(
                  "w-full rounded-2xl border bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-800 outline-none transition focus:bg-white focus:ring-4",
                  formErrors.contact_email
                    ? "border-rose-300 focus:border-rose-400 focus:ring-rose-100"
                    : "border-slate-200 focus:border-blue-400 focus:ring-blue-100",
                )}
                placeholder="client@example.com"
              />

              {formErrors.contact_email && (
                <p className="mt-1.5 text-xs font-bold text-rose-600">
                  {formErrors.contact_email}
                </p>
              )}
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-black uppercase tracking-wide text-slate-500">
                Phone
              </label>

              <input
                type="tel"
                value={formData.contact_phone}
                onChange={(event) =>
                  onChange({
                    ...formData,
                    contact_phone: event.target.value,
                  })
                }
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-800 outline-none transition focus:border-blue-400 focus:bg-white focus:ring-4 focus:ring-blue-100"
                placeholder="+254 7xx xxx xxx"
              />
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-black uppercase tracking-wide text-slate-500">
                M-Pesa Paybill
              </label>

              <input
                type="text"
                value={formData.mpesa_paybill}
                onChange={(event) =>
                  onChange({
                    ...formData,
                    mpesa_paybill: event.target.value,
                  })
                }
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-800 outline-none transition focus:border-blue-400 focus:bg-white focus:ring-4 focus:ring-blue-100"
                placeholder="Example: 123456"
              />
            </div>

            <div className="md:col-span-2">
              <label className="mb-1.5 block text-xs font-black uppercase tracking-wide text-slate-500">
                Address
              </label>

              <textarea
                value={formData.address}
                onChange={(event) =>
                  onChange({ ...formData, address: event.target.value })
                }
                className="min-h-[105px] w-full resize-none rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-800 outline-none transition focus:border-blue-400 focus:bg-white focus:ring-4 focus:ring-blue-100"
                placeholder="Office address, location, or installation region"
              />
            </div>
          </div>

          <div className="mt-5 flex flex-col-reverse gap-3 border-t border-slate-100 pt-4 sm:flex-row sm:items-center sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-black text-slate-700 transition hover:bg-slate-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-5 py-2.5 text-sm font-black text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <Loader2 size={17} className="animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check size={17} />
                  {mode === "create" ? "Create Client" : "Save Changes"}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const ClientsPage: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);

  const [formData, setFormData] = useState<ClientFormData>({
    name: "",
    contact_email: "",
    contact_phone: "",
    address: "",
    mpesa_paybill: "",
  });

  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [submitting, setSubmitting] = useState(false);
  const [viewingClient, setViewingClient] = useState<number | null>(null);
  const [showDetailPage, setShowDetailPage] = useState<boolean>(false);

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    setLoading(true);
    setErrorMessage("");

    try {
      const data = await fetchData(API_CONFIG.endpoints.clients);
      setClients(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error loading clients:", error);
      setErrorMessage("Failed to load clients. Please refresh and try again.");
    } finally {
      setLoading(false);
    }
  };

  const filteredClients = useMemo(() => {
    const value = searchTerm.trim().toLowerCase();

    if (!value) return clients;

    return clients.filter((client) => {
      return [
        client.name,
        client.contact_email,
        client.contact_phone,
        client.address,
        client.mpesa_paybill,
      ]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(value));
    });
  }, [clients, searchTerm]);

  const totalUsers = useMemo(
    () => clients.reduce((sum, client) => sum + (client.users?.length || 0), 0),
    [clients],
  );

  const totalRouters = useMemo(
    () =>
      clients.reduce((sum, client) => sum + (client.routers?.length || 0), 0),
    [clients],
  );

  const clientsWithPaybill = useMemo(
    () => clients.filter((client) => hasValue(client.mpesa_paybill)).length,
    [clients],
  );

  const openCreateModal = () => {
    setModalMode("create");
    setFormData({
      name: "",
      contact_email: "",
      contact_phone: "",
      address: "",
      mpesa_paybill: "",
    });
    setFormErrors({});
    setSelectedClient(null);
    setShowModal(true);
  };

  const openEditModal = (client: Client, event?: React.MouseEvent) => {
    event?.stopPropagation();

    setModalMode("edit");
    setSelectedClient(client);

    setFormData({
      name: client.name || "",
      contact_email: client.contact_email || "",
      contact_phone: client.contact_phone || "",
      address: client.address || "",
      mpesa_paybill: client.mpesa_paybill || "",
    });

    setFormErrors({});
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedClient(null);
    setFormData({
      name: "",
      contact_email: "",
      contact_phone: "",
      address: "",
      mpesa_paybill: "",
    });
    setFormErrors({});
  };

  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {};

    if (!formData.name?.trim()) {
      errors.name = "Client name is required.";
    }

    if (!formData.contact_email?.trim()) {
      errors.contact_email = "Email is required.";
    } else if (!/\S+@\S+\.\S+/.test(formData.contact_email)) {
      errors.contact_email = "Enter a valid email address.";
    }

    setFormErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) return;

    setSubmitting(true);
    setErrorMessage("");

    const payload = {
      name: formData.name.trim(),
      contact_email: formData.contact_email.trim(),
      contact_phone: formData.contact_phone?.trim() || "",
      address: formData.address?.trim() || "",
      mpesa_paybill: formData.mpesa_paybill?.trim() || "",
    };

    try {
      if (modalMode === "create") {
        await fetchApp(`${API_CONFIG.endpoints.clients}`, {
          method: "POST",
          body: JSON.stringify(payload),
        });
      } else if (selectedClient) {
        await fetchApp(`${API_CONFIG.endpoints.clients}/${selectedClient.id}`, {
          method: "PUT",
          body: JSON.stringify(payload),
        });
      }

      await loadClients();
      closeModal();
    } catch (error) {
      console.error("Error saving client:", error);
      setErrorMessage("Failed to save client. Please check the details again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (clientId: number, event?: React.MouseEvent) => {
    event?.stopPropagation();

    const confirmed = window.confirm(
      "Are you sure you want to delete this client?",
    );

    if (!confirmed) return;

    setErrorMessage("");

    try {
      await fetchApp(`${API_CONFIG.endpoints.clients}/${clientId}`, {
        method: "DELETE",
      });

      await loadClients();
    } catch (error) {
      console.error("Error deleting client:", error);
      setErrorMessage("Failed to delete client. Please try again.");
    }
  };

  const handleViewClient = (clientId: number, event?: React.MouseEvent) => {
    event?.stopPropagation();
    setViewingClient(clientId);
    setShowDetailPage(true);
  };

  const handleBackToList = () => {
    setShowDetailPage(false);
    setViewingClient(null);
  };

  if (showDetailPage && viewingClient) {
    return (
      <ClientDetailPage clientId={viewingClient} onBack={handleBackToList} />
    );
  }

  return (
    <div className="min-h-full w-full bg-[linear-gradient(180deg,#eef5ff_0%,#f8fbff_100%)] px-2 py-3 sm:px-3 md:px-4 md:py-4">
      <div className="w-full max-w-none space-y-4">
        <section className="relative overflow-hidden rounded-3xl bg-slate-950 p-5 text-white shadow-xl md:p-6">
          <div className="absolute right-[-80px] top-[-80px] h-64 w-64 rounded-full bg-blue-500/30 blur-3xl" />
          <div className="absolute bottom-[-100px] left-1/3 h-64 w-64 rounded-full bg-violet-500/20 blur-3xl" />

          <div className="relative flex flex-col justify-between gap-5 xl:flex-row xl:items-center">
            <div className="max-w-3xl">
              <div className="flex items-start gap-4">
                <div className="hidden rounded-3xl bg-white p-4 text-slate-950 shadow-xl sm:block">
                  <Building2 size={28} />
                </div>

                <div>
                  <h1 className="text-3xl font-black tracking-tight md:text-4xl">
                    Client Management
                  </h1>

                  <p className="mt-2 max-w-2xl text-sm leading-6 text-white/65">
                    Manage organisations, contact details, paybill information,
                    routers, users, and client-level network resources.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-3 xl:min-w-[480px]">
              <div className="rounded-2xl border border-white/10 bg-white/10 p-3 backdrop-blur">
                <div className="flex items-center gap-3">
                  <div className="rounded-xl bg-white/15 p-2 text-white">
                    <Users size={18} />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-white/60">Clients</p>
                    <p className="text-xl font-black text-white">
                      {formatNumber(clients.length)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-white/10 bg-white/10 p-3 backdrop-blur">
                <div className="flex items-center gap-3">
                  <div className="rounded-xl bg-white/15 p-2 text-white">
                    <Router size={18} />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-white/60">Routers</p>
                    <p className="text-xl font-black text-white">
                      {formatNumber(totalRouters)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-white/10 bg-white/10 p-3 backdrop-blur">
                <div className="flex items-center gap-3">
                  <div className="rounded-xl bg-white/15 p-2 text-white">
                    <CreditCard size={18} />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-white/60">
                      Paybills
                    </p>
                    <p className="text-xl font-black text-white">
                      {formatNumber(clientsWithPaybill)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard
            title="Total Clients"
            value={clients.length}
            helper="Registered organisations"
            icon={<Building2 size={23} />}
            tone="bg-blue-600"
          />

          <StatCard
            title="Linked Users"
            value={totalUsers}
            helper="Users under all clients"
            icon={<UserRound size={23} />}
            tone="bg-emerald-600"
          />

          <StatCard
            title="Routers"
            value={totalRouters}
            helper="Routers assigned to clients"
            icon={<Wifi size={23} />}
            tone="bg-violet-600"
          />

          <StatCard
            title="Paybill Ready"
            value={clientsWithPaybill}
            helper="Clients with M-Pesa paybill"
            icon={<CreditCard size={23} />}
            tone="bg-amber-500"
          />
        </section>

        <section className="rounded-3xl border border-white bg-white/95 p-4 shadow-sm backdrop-blur">
          <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-center">
            <div className="flex items-start gap-3">
              <div className="rounded-2xl bg-slate-950 p-3 text-white">
                <Search size={18} />
              </div>

              <div>
                <h2 className="font-black text-slate-950">Search & Actions</h2>
                <p className="mt-1 text-xs font-medium text-slate-400">
                  Search by client name, email, phone, address, or paybill.
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="flex min-w-0 items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3 transition focus-within:border-blue-400 focus-within:bg-white focus-within:ring-4 focus-within:ring-blue-100 sm:w-[420px]">
                <Search size={17} className="shrink-0 text-slate-400" />

                <input
                  type="text"
                  placeholder="Search clients..."
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  className="w-full border-none bg-transparent text-sm font-semibold text-slate-800 outline-none placeholder:text-slate-400"
                />

                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="rounded-full p-1 text-slate-400 transition hover:bg-slate-200 hover:text-slate-700"
                    type="button"
                  >
                    <X size={15} />
                  </button>
                )}
              </div>

              <button
                onClick={loadClients}
                disabled={loading}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-black text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <RefreshCw
                  size={17}
                  className={loading ? "animate-spin" : ""}
                />
                Refresh
              </button>

              <button
                onClick={openCreateModal}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-5 py-3 text-sm font-black text-white shadow-sm transition hover:bg-blue-700"
              >
                <Plus size={17} />
                Add Client
              </button>
            </div>
          </div>

          {errorMessage && (
            <div className="mt-4 flex items-start gap-2 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm font-bold text-rose-700">
              <AlertTriangle size={17} className="mt-0.5 shrink-0" />
              {errorMessage}
            </div>
          )}
        </section>

        {loading ? (
          <div className="flex items-center justify-center gap-3 rounded-3xl border border-white bg-white/95 px-4 py-16 text-sm font-black text-slate-500 shadow-sm">
            <Loader2 size={22} className="animate-spin text-blue-600" />
            Loading clients...
          </div>
        ) : filteredClients.length === 0 ? (
          <EmptyState searchTerm={searchTerm} onCreate={openCreateModal} />
        ) : (
          <section className="overflow-hidden rounded-3xl border border-white bg-white/95 shadow-sm backdrop-blur">
            <div className="flex flex-col justify-between gap-3 border-b border-slate-100 p-4 md:flex-row md:items-center md:p-5">
              <div className="flex items-center gap-3">
                <div className="rounded-2xl bg-blue-50 p-3 text-blue-700">
                  <ShieldCheck size={20} />
                </div>

                <div>
                  <h2 className="font-black text-slate-950">Clients List</h2>

                  <p className="text-xs font-medium text-slate-400">
                    Showing {formatNumber(filteredClients.length)} of{" "}
                    {formatNumber(clients.length)} clients
                  </p>
                </div>
              </div>

              {searchTerm && (
                <div className="rounded-2xl bg-slate-50 px-3 py-2 text-xs font-bold text-slate-500">
                  Search:{" "}
                  <span className="font-black text-slate-800">
                    “{searchTerm}”
                  </span>
                </div>
              )}
            </div>

            <div className="hidden overflow-x-auto lg:block">
              <table className="w-full min-w-[1120px] border-collapse">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/80 text-left text-[11px] font-black uppercase tracking-wide text-slate-400">
                    <th className="px-5 py-4">Client</th>
                    <th className="px-5 py-4">Contact</th>
                    <th className="px-5 py-4">Paybill</th>
                    <th className="px-5 py-4">Address</th>
                    <th className="px-5 py-4">Resources</th>
                    <th className="px-5 py-4 text-right">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredClients.map((client) => (
                    <tr
                      key={client.id}
                      className="border-b border-slate-100 transition hover:bg-slate-50/80"
                    >
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <ClientAvatar name={client.name} />

                          <div className="min-w-0">
                            <p className="max-w-[260px] truncate text-sm font-black text-slate-950">
                              {client.name}
                            </p>

                            <p className="mt-1 text-xs font-medium text-slate-400">
                              Client organisation
                            </p>
                          </div>
                        </div>
                      </td>

                      <td className="px-5 py-4">
                        <div className="space-y-2">
                          <ContactLine
                            icon={<Mail size={15} />}
                            value={client.contact_email}
                          />

                          <ContactLine
                            icon={<Phone size={15} />}
                            value={client.contact_phone}
                          />
                        </div>
                      </td>

                      <td className="px-5 py-4">
                        {hasValue(client.mpesa_paybill) ? (
                          <span className="inline-flex items-center gap-2 rounded-full border border-amber-100 bg-amber-50 px-3 py-1.5 text-xs font-black text-amber-700">
                            <CreditCard size={14} />
                            {client.mpesa_paybill}
                          </span>
                        ) : (
                          <span className="text-sm font-medium text-slate-400">
                            Not set
                          </span>
                        )}
                      </td>

                      <td className="px-5 py-4">
                        <div className="max-w-[300px]">
                          <ContactLine
                            icon={<MapPin size={15} />}
                            value={client.address}
                          />
                        </div>
                      </td>

                      <td className="px-5 py-4">
                        <div className="flex flex-wrap items-center gap-2">
                          <ResourceBadge
                            icon={<UserRound size={13} />}
                            label="users"
                            value={client.users?.length || 0}
                            className="border-emerald-100 bg-emerald-50 text-emerald-700"
                          />

                          <ResourceBadge
                            icon={<Wifi size={13} />}
                            label="routers"
                            value={client.routers?.length || 0}
                            className="border-violet-100 bg-violet-50 text-violet-700"
                          />
                        </div>
                      </td>

                      <td className="px-5 py-4 text-right">
                        <div className="inline-flex items-center gap-1 rounded-2xl border border-slate-100 bg-slate-50 p-1">
                          <button
                            onClick={(event) =>
                              handleViewClient(client.id, event)
                            }
                            className="inline-flex items-center justify-center rounded-xl p-2 text-blue-600 transition hover:bg-white hover:text-blue-700 hover:shadow-sm"
                            title="View details"
                          >
                            <Eye size={16} />
                          </button>

                          <button
                            onClick={(event) => openEditModal(client, event)}
                            className="inline-flex items-center justify-center rounded-xl p-2 text-slate-600 transition hover:bg-white hover:text-slate-950 hover:shadow-sm"
                            title="Edit client"
                          >
                            <Edit3 size={16} />
                          </button>

                          <button
                            onClick={(event) => handleDelete(client.id, event)}
                            className="inline-flex items-center justify-center rounded-xl p-2 text-rose-600 transition hover:bg-white hover:text-rose-700 hover:shadow-sm"
                            title="Delete client"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="grid gap-3 p-4 lg:hidden">
              {filteredClients.map((client) => (
                <div
                  key={client.id}
                  className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <ClientAvatar name={client.name} />

                      <div className="min-w-0">
                        <h3 className="truncate text-base font-black text-slate-950">
                          {client.name}
                        </h3>

                        <p className="text-xs font-semibold text-slate-400">
                          Client organisation
                        </p>
                      </div>
                    </div>

                    <div className="flex shrink-0 items-center gap-1 rounded-2xl border border-slate-100 bg-slate-50 p-1">
                      <button
                        onClick={(event) => handleViewClient(client.id, event)}
                        className="rounded-xl p-2 text-blue-600 hover:bg-white"
                        title="View details"
                      >
                        <Eye size={15} />
                      </button>

                      <button
                        onClick={(event) => openEditModal(client, event)}
                        className="rounded-xl p-2 text-slate-600 hover:bg-white"
                        title="Edit client"
                      >
                        <Edit3 size={15} />
                      </button>

                      <button
                        onClick={(event) => handleDelete(client.id, event)}
                        className="rounded-xl p-2 text-rose-600 hover:bg-white"
                        title="Delete client"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>

                  <div className="mt-4 grid gap-2 rounded-2xl bg-slate-50 p-3">
                    <ContactLine
                      icon={<Mail size={15} />}
                      value={client.contact_email}
                    />

                    <ContactLine
                      icon={<Phone size={15} />}
                      value={client.contact_phone}
                    />

                    <ContactLine
                      icon={<CreditCard size={15} />}
                      value={client.mpesa_paybill}
                      fallback="Paybill not set"
                    />

                    <ContactLine
                      icon={<MapPin size={15} />}
                      value={client.address}
                      fallback="Address not set"
                    />
                  </div>

                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <ResourceBadge
                      icon={<UserRound size={13} />}
                      label="users"
                      value={client.users?.length || 0}
                      className="border-emerald-100 bg-emerald-50 text-emerald-700"
                    />

                    <ResourceBadge
                      icon={<Wifi size={13} />}
                      label="routers"
                      value={client.routers?.length || 0}
                      className="border-violet-100 bg-violet-50 text-violet-700"
                    />
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      <ClientFormModal
        open={showModal}
        mode={modalMode}
        formData={formData}
        formErrors={formErrors}
        submitting={submitting}
        onClose={closeModal}
        onSubmit={handleSubmit}
        onChange={setFormData}
      />
    </div>
  );
};

export default ClientsPage;
